import React, { useEffect, useRef } from 'react';
import Classnames from 'classnames';
import { PagedListStateManager } from '../use-paged-list-state';
import { TrackedHandlerList, useIntersectionObserver, useStringSequence, useUpdateEffect } from '../../../../hooks';
import { PagedListItemProps } from '../paged-list-item-wrapper';
import { PagedListStateActionNames } from '../paged-list-reducer';
import { isHTMLElement } from '../../../../utils';

import './paged-list-content-list.scss';

const LAST_ELEMENT_TRACKING_ID = '.paged-list-item:last-child';

interface PagedListContentListProps<RowType> {
    listId: string;
    isGrid: boolean;
    dense: boolean;
    shouldShowLogo: boolean;
    ListItemComponent: React.ComponentType<PagedListItemProps<RowType>>;
    pageSelectorComponent?: JSX.Element;
    pagingStateManager: PagedListStateManager<RowType>;
    disableInfiniteScroll: boolean;
}

export function PagedListContentList<RowType>({
    listId,
    isGrid,
    dense,
    shouldShowLogo,
    ListItemComponent,
    pageSelectorComponent,
    pagingStateManager,
    disableInfiniteScroll,
}: PagedListContentListProps<RowType>): React.ReactElement {
    const idSeq = useStringSequence(listId);
    const [localState, dispatchLocalState] = pagingStateManager;

    const listClassnames = Classnames('paged-list-content-list', {
        'paged-list-content-list--grid': isGrid,
        'paged-list-content-list--dense': dense,
    });

    const handlerList = useRef<TrackedHandlerList>({});
    const handleObserverChange: IntersectionObserverCallback = (
        entries: IntersectionObserverEntry[],
        observer,
    ): void => {
        if (!entries) {
            return;
        }

        entries.forEach((entry) => {
            if (!isHTMLElement(entry.target)) {
                return;
            }

            const trackingId = entry.target.dataset.tracking_id;
            const handler = handlerList.current[trackingId];

            if (!handler) {
                return;
            }

            if (entry.isIntersecting) {
                if (isHTMLElement(entry.target)) {
                    handler.handler(observer, entry);
                }

                return;
            }

            if (handler.handlerValid) {
                return;
            }

            handler.observe();
        });
    };

    const handleListTop = () => {
        if (pageSelectorComponent) {
            dispatchLocalState({
                type: PagedListStateActionNames.DECREMENT_FIRST_PAGE,
            });
        }
    };

    const topRef = useRef(null);
    const { observer, setTopElement, trackElement, trackedHandlers } = disableInfiniteScroll
        ? {
              observer: null,
              setTopElement: null,
              trackElement: null,
              trackedHandlers: null,
          }
        : useIntersectionObserver(handleListTop);

    const handleLastElementIntersect = (entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
            if (localState.pageManager.pagesLeftToLoad > 0) {
                dispatchLocalState({ type: PagedListStateActionNames.SET_NEEDS_PAGE });
            }

            return true;
        }

        return false;
    };

    useEffect(() => {
        if (!setTopElement) {
            return;
        }

        setTopElement(topRef.current, handleObserverChange, {
            threshold: [0.05, 1.0],
        });
    }, []);

    useEffect(() => {
        if (observer) {
            trackElement(LAST_ELEMENT_TRACKING_ID, handleLastElementIntersect);
        }
    }, [observer]);

    useEffect(() => {
        handlerList.current = trackedHandlers;
    }, [trackedHandlers]);

    useEffect(() => {
        if (localState.pageManager.length === 0) {
            if (localState.pageManager.pagesLeftToLoad > 0) {
                dispatchLocalState({ type: PagedListStateActionNames.SET_NEEDS_PAGE });
            }

            return;
        }

        if (localState.isEmpty || localState.isError) {
            return;
        }

        if (!handlerList.current) {
            return;
        }

        if (handlerList.current[LAST_ELEMENT_TRACKING_ID]) {
            handlerList.current[LAST_ELEMENT_TRACKING_ID].observe();
        }
    }, [localState.pageManagerHash]);

    useUpdateEffect(() => {
        // This is not ideal, but hopefully we can fix the way we observe
        // page transitions to do this better.
        localState.pageManager.clearAllPages();

        if (!localState.pageManager.getPage(localState.pageManager.startingPage)) {
            dispatchLocalState({ type: PagedListStateActionNames.SET_NEEDS_PAGE });
        }

        if (!handlerList.current) {
            return;
        }

        if (handlerList.current[LAST_ELEMENT_TRACKING_ID]) {
            handlerList.current[LAST_ELEMENT_TRACKING_ID].observe();
        }
    }, [localState.firstPageNumber]);

    const renderData = () => {
        return localState.pageManager.map((dataItem, _index: number) => {
            const id = idSeq();

            if (Array.isArray(dataItem.row)) {
                return null;
            }

            return (
                <ListItemComponent
                    key={id}
                    id={id}
                    data={dataItem.row}
                    shouldShowLogo={shouldShowLogo}
                    dense={dense}
                    pageNumber={dataItem.pageNumber}
                    pageIndex={dataItem.pageIndex}
                />
            );
        });
    };

    return (
        <ul id={listId} className={listClassnames} ref={topRef} data-testid="paged-list-content-list">
            {renderData()}
        </ul>
    );
}
